
import { supabase } from "@/integrations/supabase/client";
import { debugLog, DebugLevel } from "@/utils/debugUtils";
import { createNewRound } from "@/utils/gameCommands";

/**
 * Ensures a round exists in the database for the next round
 */
export const ensureNextRoundExists = async (
  gameId: string,
  nextRoundNumber: number
): Promise<boolean> => {
  try {
    // Verify if the round already exists before trying to create it
    const { data: existingRound } = await supabase
      .from("rounds")
      .select("id")
      .eq("game_id", gameId)
      .eq("round_number", nextRoundNumber)
      .maybeSingle();
      
    if (!existingRound) {
      debugLog(DebugLevel.INFO, "roundPreparation", `Creating round ${nextRoundNumber} in database`);
      await createNewRound(gameId, nextRoundNumber);
      debugLog(DebugLevel.INFO, "roundPreparation", `Successfully created round ${nextRoundNumber} in database`);
    } else {
      debugLog(DebugLevel.INFO, "roundPreparation", `Round ${nextRoundNumber} already exists, continuing...`);
    }
    
    return true;
  } catch (createError) {
    debugLog(DebugLevel.ERROR, "roundPreparation", "Error creating new round", createError);
    
    // Check if round already exists before giving up
    const { data: existingRound } = await supabase
      .from("rounds")
      .select("id")
      .eq("game_id", gameId)
      .eq("round_number", nextRoundNumber)
      .maybeSingle();
      
    if (!existingRound) {
      debugLog(DebugLevel.CRITICAL, "roundPreparation", "Round doesn't exist and couldn't be created", { gameId, roundNumber: nextRoundNumber });
      throw createError; // Re-throw if round doesn't exist
    } else {
      debugLog(DebugLevel.WARN, "roundPreparation", `Round ${nextRoundNumber} already exists, continuing...`);
      return true;
    }
  }
};

/**
 * Updates the database with the AI's word for the next round
 */
export const saveAiWordForNextRound = async (
  gameId: string,
  roundNumber: number,
  aiWord: string,
  aiExplanation: string | undefined
): Promise<boolean> => {
  try {
    debugLog(DebugLevel.INFO, "roundPreparation", `Saving AI word '${aiWord}' for round ${roundNumber}`);
    
    const { error: nextWordError } = await supabase
      .from("rounds")
      .update({ 
        ai_word: aiWord,
        ai_explanation: aiExplanation
      })
      .eq("game_id", gameId)
      .eq("round_number", roundNumber);
      
    if (nextWordError) {
      debugLog(DebugLevel.ERROR, "roundPreparation", "Error updating next round", nextWordError);
      return false;
    } else {
      debugLog(DebugLevel.INFO, "roundPreparation", `Successfully saved AI word '${aiWord}' to database`);
      return true;
    }
  } catch (error) {
    debugLog(DebugLevel.ERROR, "roundPreparation", "Error saving AI word", error);
    return false;
  }
};
